import React, { useState, useRef, useEffect } from "react";
import logo from "../assets/images/logo.png";
import logoInverted from "../assets/images/logoInverted.png";
import facebook from "../assets/images/facebook.png";
import youtube from "../assets/images/youtube.png";
import instagram from "../assets/images/instagram.png";
import twitter from "../assets/images/twitter.png";
import mail from "../assets/images/mail.png";
import { Link, useNavigate } from "react-router-dom";
import { RiAdminLine } from "react-icons/ri";
import { IoIosArrowDown } from "react-icons/io";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai"; // Icons for the menu
import { useUser } from "../context/UserContext";
import apiClient from "../api/axiosInstance";

const Header = () => {
  const { user, setUser } = useUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // State to handle menu overlay
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("username");
    if (storedUser) {
      setUser({ username: storedUser });
    }
  }, [setUser]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleUserClick = () => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    } else {
      navigate("/hr-management");
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post("api/auth/logout");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("username");
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("username");
      setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-white shadow-md">
      <header className="border-b border-gray-200">
        <nav
          className="mx-auto flex max-w-screen-2xl items-center justify-between gap-x-6 py-1 px-6 sm:px-5 lg:py-1"
          aria-label="Global"
        >
          {/* Logo Section */}
          <div className="flex items-center w-auto">
            <Link
              to="/"
              className="flex-none flex-shrink-0 block p-1 rounded-lg"
              title="Home page"
            >
              <img
                className="block w-auto h-[64px] xs:h-20 xl:h-24"
                src={logo}
                alt="EAC Logo"
              />
            </Link>
            <div className="text-gray-500 ml-3 mr-8 mt-3 hidden md:block">
              <p className="text-xs">
                EAC Close, Afrika Mashariki Road <br />
                P.O. Box 1096 <br />
                ARUSHA, TANZANIA <br />
                E-mail:{" "}
                <a
                  href="mailto:eac@eachq.org"
                  className="text-blue-600 hover:underline"
                >
                  eac@eachq.org
                </a>
              </p>
            </div>
          </div>

          <div className="text-center lg:pl-8">
            <h1 className="text-green-700 font-bold text-base sm:text-lg md:text-3xl">
              East African Community Secretariat
            </h1>
            <p className="text-[0.65rem] italic mt-1 sm:text-xs md:text-sm">
              'One People, One Destiny'
            </p>
          </div>

          {/* Hamburger Menu Icon for small screens */}
          <div className="md:hidden">
            {!menuOpen && (
              <button onClick={() => setMenuOpen(true)} aria-label="Open Menu">
                <AiOutlineMenu size={30} className="text-green-700" />
              </button>
            )}
          </div>

          {/* Social Media and Admin Login - Hidden on small screens */}
          <div className="hidden lg:flex items-center justify-end gap-2">
            <div ref={dropdownRef} className="relative">
              <div
                className="whitespace-nowrap inline-block mt-10 px-4 py-1.5 text-base font-semibold bg-white text-gray-500 hover:text-green-700 cursor-pointer"
                onClick={handleUserClick}
                title="Admin Login"
              >
                <RiAdminLine className="inline mr-3 mb-1" size={23} />
                {user ? (
                  <>
                    {user.username}{" "}
                    <IoIosArrowDown
                      className="text-red-500 inline"
                      onClick={toggleDropdown}
                    />
                  </>
                ) : (
                  "Admin Login"
                )}
              </div>

              {user && dropdownOpen && (
                <div className="absolute top-[70px] right-10 mt-2 w-32 font-semibold bg-gray-400 border border-gray-500 rounded-md shadow-lg z-50">
                  <button
                    onClick={handleLogout}
                    className="block px-8 py-2 text-gray-700 hover:bg-red-500 hover:text-white w-full text-left"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Social Media Icons */}
            <div className="flex md:flex-col gap-3 my-2">
              <p className="font-semibold pl-2">Find Us on</p>
              <div className="flex gap-3">
                <a
                  href="https://x.com/jumuiya"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:animate-popout"
                >
                  <img
                    src={twitter}
                    alt="Twitter"
                    className="h-[30px] w-[30px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                  />
                </a>
                <a
                  href="https://www.facebook.com/proudlyeastafrican"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:animate-popout"
                >
                  <img
                    src={facebook}
                    alt="Facebook"
                    className="h-[30px] w-[30px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                  />
                </a>
                <a
                  href="https://www.instagram.com/eac_secretariat1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:animate-popout"
                >
                  <img
                    src={instagram}
                    alt="Instagram"
                    className="h-[30px] w-[30px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                  />
                </a>
                <a
                  href="https://www.youtube.com/channel/UC_Nt3M0n4ftThoVVQMK_D3A"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:animate-popout"
                >
                  <img
                    src={youtube}
                    alt="YouTube"
                    className="h-[30px] w-[30px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                  />
                </a>
                <a
                  href="mailto:eac@eachq.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:animate-popout"
                >
                  <img
                    src={mail}
                    alt="Email"
                    className="h-[30px] w-[30px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                  />
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {menuOpen && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-85 z-50 flex flex-col pt-24 items-center space-y-8">
            {/* Close Button - Positioned in top right corner */}
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-4 right-4 text-white z-50"
            >
              <AiOutlineClose size={30} />
            </button>

            {/* logo above menu options */}
            <Link
              to="/"
              className="flex-none flex-shrink-0 block rounded-lg"
              title="Home page"
            >
              <img
                className="block w-auto h-[90px]"
                src={logoInverted}
                alt="EAC Logo"
              />
            </Link>

            {/* Admin Login / Logged in User */}
            <div
              className="whitespace-nowrap text-blue-300 text-lg font-semibold cursor-pointer"
              onClick={handleUserClick}
            >
              <RiAdminLine className="inline mr-3 text-white" size={23} />
              {user ? user.username : "Admin Login"}
            </div>

            {/* Conditional Logout Option - Only shows when a user is logged in */}
            {user && (
              <button
                onClick={handleLogout}
                className="text-red-500 text-lg font-semibold mt-2"
              >
                Logout
              </button>
            )}

            {/* About Us */}
            <Link
              to="https://www.eac.int"
              className="text-white text-lg font-semibold"
            >
              About Us
            </Link>

            {/* Contact Us */}
            <Link
              to="https://www.eac.int/contacts"
              className="text-white text-lg font-semibold"
            >
              Contact Us
            </Link>

            {/* Social Media Icons */}
            <div className="flex space-x-4">
              <a
                href="https://x.com/jumuiya"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:animate-popout"
              >
                <img
                  src={twitter}
                  alt="Twitter"
                  className="h-[35px] w-[35px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                />
              </a>
              <a
                href="https://www.facebook.com/proudlyeastafrican"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:animate-popout"
              >
                <img
                  src={facebook}
                  alt="Facebook"
                  className="h-[35px] w-[35px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                />
              </a>
              <a
                href="https://www.instagram.com/eac_secretariat1/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:animate-popout"
              >
                <img
                  src={instagram}
                  alt="Instagram"
                  className="h-[35px] w-[35px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UC_Nt3M0n4ftThoVVQMK_D3A"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:animate-popout"
              >
                <img
                  src={youtube}
                  alt="YouTube"
                  className="h-[35px] w-[35px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                />
              </a>
              <a
                href="mailto:eac@eachq.org"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:animate-popout"
              >
                <img
                  src={mail}
                  alt="Email"
                  className="h-[35px] w-[35px] transition-transform duration-300 ease-in-out hover:scale-110 hover:brightness-125 active:scale-95"
                />
              </a>
            </div>

            <div className="text-center text-xs text-white">
              <p>EAC Close, Afrika Mashariki Road,</p>
              <p>P.O. Box 1096 ,</p>
              <p>ARUSHA, TANZANIA</p>
            </div>
          </div>
        )}
      </header>
    </div>
  );
};

export default Header;
